import { Component } from "react";
import styled, { css } from "styled-components/macro";
import { darkModeTextColor } from "constants/colors";
import * as Sentry from "@sentry/react";
import { ONE_HOUR_OF_MS } from "utils/timeUtils";
import { getTokenInfo } from "contexts/AuthManager/Auth";
import { decodeToken } from "contexts/AuthManager/jwtHelper";

const ErrorBoundaryStyles = styled.h1<{
  $small: boolean;
  $overlay: boolean;
  $isDarkMode?: boolean;
}>`
  padding: 6px;
  text-align: center;
  box-sizing: border-box;

  ${props =>
    props.$small
      ? css`
          height: fit-content;
          width: fit-content;
          margin: auto;
        `
      : css`
          height: 100%;
          width: 100%;
        `}
  ${props =>
    props.$overlay
      ? css`
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          position: absolute;
          z-index: 999;
          background: white;
        `
      : ""}
  .something-went-wrong {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  button {
    background: none;
    border: none;
    text-decoration: underline;
    color: inherit;
    font-size: 100%;
    cursor: pointer;
    color: #1a0dab;
  }
  ${({ $isDarkMode }) =>
    $isDarkMode
      ? `
    color: ${darkModeTextColor};`
      : ""}
`;

/** Sentry exposes a Sentry.ErrorBoundary component as well, if we want to send bounded errors to Sentry
 * https://docs.sentry.io/platforms/javascript/guides/react/#add-react-error-boundary
 */
export class ErrorBoundary extends Component<{
  ignore?: boolean;
  fallback?: React.ReactNode;
  small?: boolean;
  overlay?: boolean;
  id?: string;
  children: React.ReactNode;
}> {
  state = {
    hasError: false,
  };

  static getDerivedStateFromError() {
    return {
      hasError: true,
    };
  }

  componentDidCatch(error: any, info: any) {
    if (!this.props.ignore) {
      console.log({ error, info });
      Sentry.captureException(new Error(error), {
        tags: {
          errorId: "flight_error_boundary",
          errorBoundaryId: this.props.id,
        },
        extra: {
          error,
          info,
        },
      });
    }
  }

  render() {
    const root = document.getElementById("root");
    const isDarkMode = root?.classList.contains("dark-mode");
    const isRavenUser = getIsRavenUser();

    if (this.state.hasError) {
      autoRetryOncePerHour();

      return (
        <ErrorBoundaryStyles
          $isDarkMode={isDarkMode}
          $small={this.props.small ?? false}
          $overlay={this.props.overlay ?? false}
        >
          {this.props.fallback || (
            <div className="something-went-wrong">
              Something went wrong --{" "}
              <button onClick={() => window.location.reload()}>
                click to retry
              </button>
              <button
                onClick={() => {
                  // raven users may have overfilled their localstorage with e.g. cached processes or other customer info
                  if (isRavenUser) {
                    window.localStorage.clear();
                  }
                  window.location.reload();
                }}
              >
                clear storage and retry
              </button>
            </div>
          )}
        </ErrorBoundaryStyles>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;

function getIsRavenUser() {
  const idToken = getTokenInfo().idToken;
  const tokenDecoded = idToken ? decodeToken(idToken) : false;
  const isRavenUser =
    tokenDecoded?.["https://raven.ai/email"]?.includes("@raven");
  return isRavenUser;
}

/** autoretry once per hour — if an ErrorBoundary component is rendered and hasn't been rendered in the last hour, autoreload the page once */
function autoRetryOncePerHour(): boolean | void {
  const hasAutoRetriedRecentlyLS = window.localStorage.getItem(
    "hasAutoRetriedRecently"
  );
  const hasAutoRetriedRecently =
    hasAutoRetriedRecentlyLS &&
    Date.now() - Number(hasAutoRetriedRecentlyLS) < ONE_HOUR_OF_MS;
  const lastAutoretryDate = new Date(Number(hasAutoRetriedRecentlyLS));
  if (hasAutoRetriedRecently) {
    console.log(
      `has auto-retried recently (${lastAutoretryDate}), not reloading`
    );
    return false;
  } else {
    console.log(`hasn't auto-retried since ${lastAutoretryDate}, reloading`);
    window.localStorage.setItem(
      "hasAutoRetriedRecently",
      Date.now().toString()
    );
    window.location.reload();
  }
}
